import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTracking from 'hooks/useTracking';
import { ExtraServiceCheckout } from 'components/ExtraService';
import { navigateToSingleCheckout } from 'libs/utils/checkout';
import { clickEvent } from 'libs/common/event-tracker/events';
import { ExtraServiceOrderType, ExtraServiceCheckoutModal } from 'constants/extra-service';
import { Screen } from 'constants/tracking/screens';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { UiState } from 'constants/ui';
import { actions as closetPromotionActions } from 'state/closet-promotion/slice';
import * as closetPromotionStatelessActions from 'state/closet-promotion/actions';
import * as closetPromotionSelectors from 'state/closet-promotion/selectors';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import ClosetPromotionOrderConfirmationModal from './ClosetPromotionOrderConfirmationModal';
import ClosetPromotionPreCheckoutModal from './ClosetPromotionPreCheckoutModal';
import ClosetPromotionPreCheckoutHelpModal from './ClosetPromotionPreCheckoutHelpModal';
import ClosetPromotionDynamicPricingInfoModal from './ClosetPromotionDynamicPricingInfoModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const ClosetPromotionCheckout = _ref => {
  let {
    onCheckoutSuccess
  } = _ref;
  const dispatch = useDispatch();
  const {
    track
  } = useTracking();
  const {
    0: isProceedToCheckoutClicked,
    1: setIsProceedToCheckoutClicked
  } = useState(false);
  const order = useSelector(closetPromotionSelectors.getClosetPromotionOrder);
  const activeModal = useSelector(closetPromotionSelectors.getActiveCheckoutModal);
  const closetPromotionPricing = useSelector(closetPromotionSelectors.getPricing);
  const orderPrepareUiState = useSelector(closetPromotionSelectors.getOrderPrepareUiState);
  const isUnifiedCheckoutEnabled = useSelector(getIsFeatureSwitchEnabled('unified_checkout_screen_web'));
  const setActiveModal = useCallback(modal => {
    dispatch(closetPromotionActions.setCheckoutModal({
      modal
    }));
  }, [dispatch]);
  useEffect(function startCheckoutAfterOrderPrepare() {
    if (!order) return;
    if (!isProceedToCheckoutClicked) return;
    if (orderPrepareUiState !== UiState.Success) return;
    track(clickEvent({
      screen: Screen.ClosetPromotionOrderReview,
      target: ClickableElement.ReviewClosetPromotionOrder,
      targetDetails: "".concat(order.id)
    }));

    if (isUnifiedCheckoutEnabled) {
      if (!order) return;
      navigateToSingleCheckout(order === null || order === void 0 ? void 0 : order.id, ExtraServiceOrderType.ClosetPromotion);
    } else {
      setActiveModal(ExtraServiceCheckoutModal.Checkout);
    }

    setIsProceedToCheckoutClicked(false);
  }, [order, track, setActiveModal, isUnifiedCheckoutEnabled, isProceedToCheckoutClicked, orderPrepareUiState]);

  function openPreCheckoutModal() {
    setActiveModal(ExtraServiceCheckoutModal.PreCheckout);
  }

  function openDynamicPricingInfoModal() {
    setActiveModal(ExtraServiceCheckoutModal.DynamicPricingInfo);
    track(clickEvent({
      screen: Screen.ClosetPromotionOrderReview,
      target: ClickableElement.ClosetPromotionDynamicPricingInfo
    }));
  }

  function closeCheckoutModal() {
    setActiveModal(ExtraServiceCheckoutModal.None);
  }

  function handleCheckoutFinish(isCheckoutSuccess) {
    closeCheckoutModal();

    if (isCheckoutSuccess) {
      onCheckoutSuccess();
    }
  }

  function handleCheckoutInitAfterCardAuth(orderId) {
    dispatch(closetPromotionActions.getPricingRequest());
    dispatch(closetPromotionActions.getClosetPromotionOrderRequest({
      closetPromotionOrderId: orderId
    }));
    setActiveModal(ExtraServiceCheckoutModal.Checkout);
  }

  function handlePreCheckoutConfirm() {
    dispatch(closetPromotionStatelessActions.proceedToCheckout());
    setIsProceedToCheckoutClicked(true);
  }

  function handlePreCheckoutPreview() {
    track(clickEvent({
      screen: Screen.ClosetPromotionOrderReview,
      target: ClickableElement.ClosetPromotionPreCheckoutHelp
    }));
    setActiveModal(ExtraServiceCheckoutModal.PreCheckoutHelp);
  }

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [closetPromotionPricing && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ClosetPromotionPreCheckoutModal, {
        pricing: closetPromotionPricing,
        show: activeModal === ExtraServiceCheckoutModal.PreCheckout,
        onNextAction: handlePreCheckoutConfirm,
        onPreview: handlePreCheckoutPreview,
        onDynamicPricingInfo: openDynamicPricingInfoModal,
        onBack: closeCheckoutModal
      }), /*#__PURE__*/_jsx(ClosetPromotionPreCheckoutHelpModal, {
        show: activeModal === ExtraServiceCheckoutModal.PreCheckoutHelp,
        onBack: openPreCheckoutModal
      }), /*#__PURE__*/_jsx(ClosetPromotionDynamicPricingInfoModal, {
        show: activeModal === ExtraServiceCheckoutModal.DynamicPricingInfo,
        onBack: openPreCheckoutModal
      })]
    }), /*#__PURE__*/_jsx(ExtraServiceCheckout, {
      show: activeModal === ExtraServiceCheckoutModal.Checkout && !isUnifiedCheckoutEnabled,
      orderType: ExtraServiceOrderType.ClosetPromotion,
      orderId: order === null || order === void 0 ? void 0 : order.id,
      orderPayable: order === null || order === void 0 ? void 0 : order.payable,
      addCardSourceParams: {
        type: ExtraServiceOrderType.ClosetPromotion
      },
      onBack: openPreCheckoutModal,
      onCheckoutFinish: handleCheckoutFinish,
      onInitStartAfterCardAuth: handleCheckoutInitAfterCardAuth,
      orderConfirmationModal: ClosetPromotionOrderConfirmationModal
    })]
  });
};

export default ClosetPromotionCheckout;